import { useState } from "react";
import axios from "axios";
import Seo from "@/components/Seo";
import { Eyebrow, Section } from "@/components/Type";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = ["General", "Partnership", "Product", "Research", "Media", "Careers", "Other"];

const inputClass =
  "w-full bg-transparent border border-[#26241d] px-4 py-3 font-mono text-xs tracking-[0.1em] text-[#f5f2ea] placeholder:text-[#57534a] focus:outline-none focus:border-[#C9A227] transition-colors";

const labelClass = "block font-mono text-[9px] tracking-[0.3em] uppercase text-[#57534a] mb-2";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    reason: "General",
    message: "",
    website: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, reason: form.reason.toLowerCase() });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact | W8ED"
        description="Let's talk — partnerships, research, products, media, careers or simply curiosity. W8ED PRIVATE LIMITED, Bengaluru, India."
        path="/contact"
      />
      <section className="px-6 pb-20 pt-32 sm:pt-40">
        <div className="mx-auto max-w-6xl">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-[#f5f2ea] sm:text-6xl">
            LET'S TALK.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-[#8e8a80]">
            Whether you're building with us, researching with us, partnering with us, working with
            us or simply want to know more — we'd like to hear from you.
          </p>
        </div>
      </section>

      <Section>
        <div className="grid gap-16 lg:grid-cols-2">
          {/* company details */}
          <div>
            <p className="font-mono text-[9px] tracking-[0.3em] text-[#57534a]">COMPANY</p>
            <address className="mt-4 space-y-2 font-mono text-[11px] not-italic leading-relaxed tracking-[0.1em] text-[#8e8a80]">
              <p className="text-[#f5f2ea]">W8ED PRIVATE LIMITED</p>
              <p>CIN: U62099KA2026PTC227613</p>
            </address>

            <p className="mt-10 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">OFFICE</p>
            <address className="mt-4 space-y-2 font-mono text-[11px] not-italic leading-relaxed tracking-[0.1em] text-[#8e8a80]">
              <p>WeWork – 37 Cunningham</p>
              <p>Cunningham Road, No. 37, Cunningham Cross Road</p>
              <p>SRT Road, Vasanth Nagar</p>
              <p>Bengaluru, Karnataka 560052</p>
              <p>India</p>
            </address>

            <p className="mt-10 font-mono text-[9px] tracking-[0.3em] text-[#57534a]">REACH US</p>
            <div className="mt-4 space-y-2 font-mono text-[11px] tracking-[0.1em] text-[#8e8a80]">
              <p>
                <a href="mailto:support@w8ed.com" className="transition-colors hover:text-[#C9A227]">
                  support@w8ed.com
                </a>
              </p>
              <p>
                <a href="tel:+917483549690" className="transition-colors hover:text-[#C9A227]">
                  7483549690
                </a>
              </p>
              <p>www.w8ed.com</p>
            </div>

            <div className="mt-10">
              <iframe
                title="W8ED office — WeWork 37 Cunningham Road, Vasanth Nagar, Bengaluru"
                src="https://www.google.com/maps?q=WeWork+37+Cunningham+Road,+Vasanth+Nagar,+Bengaluru+560052&output=embed"
                className="h-72 w-full border border-[#1f1e1a] grayscale contrast-125"
                loading="lazy"
              />
              <a
                href="https://www.google.com/maps/search/?api=1&query=WeWork+37+Cunningham+Road,+Vasanth+Nagar,+Bengaluru+560052"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="get-directions"
                className="mt-4 inline-block border border-[#3a3524] px-5 py-3 font-mono text-[10px] tracking-[0.3em] text-[#C9A227] transition-colors hover:border-[#C9A227]"
              >
                GET DIRECTIONS →
              </a>
            </div>
          </div>

          {/* form */}
          <div>
            {sent ? (
              <div data-testid="contact-success" className="border border-[#1f1e1a] p-10 text-center">
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#f5f2ea]">
                  MESSAGE RECEIVED.
                </h2>
                <p className="mt-3 font-mono text-[11px] tracking-[0.3em] uppercase text-[#C9A227]">
                  We'll be in touch.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className={labelClass}>Name</label>
                  <input id="contact-name" required data-testid="contact-name" value={form.name} onChange={set("name")} className={inputClass} />
                </div>
                <div>
                  <label htmlFor="contact-email" className={labelClass}>Email</label>
                  <input id="contact-email" type="email" required data-testid="contact-email" value={form.email} onChange={set("email")} className={inputClass} />
                </div>
                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-phone" className={labelClass}>Phone — optional</label>
                    <input id="contact-phone" type="tel" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className={labelClass}>Company — optional</label>
                    <input id="contact-company" data-testid="contact-company" value={form.company} onChange={set("company")} className={inputClass} />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-reason" className={labelClass}>Reason for contact</label>
                  <select
                    id="contact-reason"
                    data-testid="contact-reason"
                    value={form.reason}
                    onChange={set("reason")}
                    className={`${inputClass} appearance-none cursor-pointer`}
                  >
                    {REASONS.map((r) => (
                      <option key={r} value={r} className="bg-[#0a0a09]">
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className={labelClass}>Message</label>
                  <textarea
                    id="contact-message"
                    required
                    rows={5}
                    data-testid="contact-message"
                    value={form.message}
                    onChange={set("message")}
                    className={`${inputClass} resize-y normal-case`}
                  />
                </div>

                {/* honeypot — invisible to humans */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={set("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />

                {error && (
                  <p data-testid="contact-error" className="font-mono text-[10px] tracking-[0.15em] uppercase text-red-500">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="w-full bg-[#f5f2ea] text-[#0a0a09] font-mono text-[11px] tracking-[0.3em] uppercase py-3.5 hover:bg-[#C9A227] transition-colors disabled:opacity-50"
                >
                  {submitting ? "Sending..." : "SEND MESSAGE"}
                </button>
              </form>
            )}
          </div>
        </div>
      </Section>
      <div className="py-16" />
    </>
  );
}
